import { minBy, sum, uniq } from 'es-toolkit';

import { LineSegment2D, Point2D } from '@paragon/geometry-library';
import { sortBy } from '@paragon/utilities';

import { LabelSize } from './util/LabelVisual';

interface BoundingBox {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export interface LabelInfo {
  readonly id: string;
  readonly center: Point2D;
  readonly size: LabelSize;
  readonly getMovementSegment: (currentPosition: Point2D) => LineSegment2D;
}

class Label {
  readonly id: string;
  readonly size: LabelSize;
  readonly getMovementSegment: (currentPosition: Point2D) => LineSegment2D;

  center: Point2D;

  get minX() {
    return this.center.x - this.size.width / 2;
  }

  get minY() {
    return this.center.y - this.size.height / 2;
  }

  get maxX() {
    return this.center.x + this.size.width / 2;
  }

  get maxY() {
    return this.center.y + this.size.height / 2;
  }

  constructor(labelInfo: LabelInfo) {
    this.id = labelInfo.id;
    this.center = labelInfo.center;
    this.size = labelInfo.size;
    this.getMovementSegment = labelInfo.getMovementSegment;
  }

  intersectsBox(box: BoundingBox) {
    return !(box.maxX < this.minX || box.minX > this.maxX || box.maxY < this.minY || box.minY > this.maxY);
  }

  overlapArea(box: BoundingBox) {
    const minX = Math.max(this.minX, box.minX);
    const minY = Math.max(this.minY, box.minY);
    const maxX = Math.min(this.maxX, box.maxX);
    const maxY = Math.min(this.maxY, box.maxY);

    const width = maxX - minX;
    const height = maxY - minY;

    return width * height;
  }
}

export function attemptToEliminateOverlap(inputLabels: readonly LabelInfo[]): { [id: string]: Point2D | undefined } {
  if (uniq(inputLabels.map((label) => label.id)).length !== inputLabels.length) {
    throw new Error('Expected each label to have a unique id.');
  }

  const labels = inputLabels.map((label) => new Label(label));

  const maxIterations = 5;
  let i = 0;
  let overlappingLabels = getOverlappingLabels(labels, labels);
  while (overlappingLabels.length > 0) {
    for (const overlappingLabel of overlappingLabels) {
      optimizeSingleLabelPosition(overlappingLabel, labels);
    }
    i += 1;
    if (i === maxIterations) break;
    overlappingLabels = getOverlappingLabels(overlappingLabels, labels);
  }

  return Object.fromEntries(labels.map((label) => [label.id, label.center] as const));
}

function getOverlappingLabels(labelsToCheck: readonly Label[], otherLabels: readonly Label[]): Label[] {
  const overlappingLabels: Label[] = [];
  for (const labelToCheck of labelsToCheck) {
    for (const otherLabel of otherLabels) {
      if (otherLabel === labelToCheck) continue;
      const isOverlap = labelToCheck.intersectsBox(otherLabel);
      if (isOverlap) {
        overlappingLabels.push(labelToCheck);
        overlappingLabels.push(otherLabel);
      }
    }
  }

  return uniq(overlappingLabels);
}

function optimizeSingleLabelPosition(label: Label, labels: readonly Label[]) {
  const { center, size, getMovementSegment } = label;
  const movementSegment = getMovementSegment(center);

  // Get boxes that overlap the movement segment
  const { width: labelOneWidth, height: labelOneHeight } = size;
  const movementSegmentOneBox = getBoxForMovementSegment(movementSegment, labelOneWidth, labelOneHeight);
  const labelsThatOverlapMovementSegment = labels.filter(
    (otherLabel) => otherLabel !== label && otherLabel.intersectsBox(movementSegmentOneBox),
  );

  // Get potential position points
  const labelOneBuffer = labelOneWidth * 3 > movementSegment.length ? 0 : labelOneWidth / 2;
  const potentialPositions: Point2D[] = [];
  for (
    let distanceAlongMovementSegmentOne = labelOneBuffer;
    distanceAlongMovementSegmentOne < movementSegment.length - labelOneBuffer;
    distanceAlongMovementSegmentOne += 1
  ) {
    const pointOnMovementSegment = movementSegment.getPointAt(distanceAlongMovementSegmentOne);
    if (!pointOnMovementSegment) continue;
    potentialPositions.push(pointOnMovementSegment);
  }

  // Sort potential position points by proximity to original position
  const potentialPositionsSorted = sortBy(potentialPositions, (potentialPosition) =>
    potentialPosition.distanceTo(center),
  );

  // Test potential position points, recording overlap area along the way
  const potentialPositionsWithOverlapArea: { position: Point2D; overlapArea: number }[] = [];
  let overlapsOtherLabels = true;
  for (const potentialPosition of potentialPositionsSorted) {
    label.center = new Point2D(potentialPosition.x, potentialPosition.y);

    const otherLabelsIntersected = labelsThatOverlapMovementSegment.filter((otherLabel) =>
      otherLabel.intersectsBox(label),
    );
    if (otherLabelsIntersected.length === 0) {
      overlapsOtherLabels = false;
      break;
    }
    potentialPositionsWithOverlapArea.push({
      position: potentialPosition,
      overlapArea: sum(otherLabelsIntersected.map((otherLabel) => label.overlapArea(otherLabel))),
    });
  }

  // If there is still overlap, choose the position with the least overlap
  if (overlapsOtherLabels && potentialPositionsWithOverlapArea.length) {
    const { position: bestPosition } = minBy(potentialPositionsWithOverlapArea, (position) => position.overlapArea)!;
    label.center = bestPosition;
  }
}

function getBoxForMovementSegment(
  movementSegment: LineSegment2D,
  labelWidth: number,
  labelHeight: number,
): BoundingBox {
  const halfLabelWidth = labelWidth / 2;
  const halfLabelHeight = labelHeight / 2;

  const { basePoint, endPoint } = movementSegment;

  const basePointMinX = basePoint.x - halfLabelWidth;
  const basePointMinY = basePoint.y - halfLabelHeight;
  const basePointMaxX = basePoint.x + halfLabelWidth;
  const basePointMaxY = basePoint.y + halfLabelHeight;

  const endPointMinX = endPoint.x - halfLabelWidth;
  const endPointMinY = endPoint.y - halfLabelHeight;
  const endPointMaxX = endPoint.x + halfLabelWidth;
  const endPointMaxY = endPoint.y + halfLabelHeight;

  return {
    minX: Math.min(basePointMinX, endPointMinX),
    minY: Math.min(basePointMinY, endPointMinY),
    maxX: Math.max(basePointMaxX, endPointMaxX),
    maxY: Math.max(basePointMaxY, endPointMaxY),
  };
}
